import React, { useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import { ListCandidate } from '../../../../../../entities/applicant_tracking/ListCandidate';
import { TableBody } from './TableBody';
import { TableHeader } from './TableHeader';
import { TableFooter } from './TableFooter';
import { FilterStates } from '../../FilterStates';
import { User } from '../../../../../../entities/User';
import { HrisEmployee } from '../../../../../../entities/applicant_tracking/HrisEmployees';
import { HrisProvider } from '../../../../../../enums/IntegrationProvider/HrisProvider';
import { AlertObject } from '../../../../../../components/Alert';
import { EmailAccountIntegration } from '../../../../../../entities/EmailAccountIntegration';
import { useFeatureFlags } from '../../../../../../contexts/FeatureFlagContext';
import { EditablePushToHrisModal } from '../../../../../CandidatePage/CandidateActions/EditablePushToHrisModal';
import { PushToHrisModal } from '../../../../../CandidatePage/CandidateActions/PushToHrisModal';

interface PropTypes {
  atsSignableOfferEnabled: boolean;
  candidates: ListCandidate[];
  currentUser: User;
  emptyState: React.ReactNode;
  emailAccountIntegration: EmailAccountIntegration;
  filterStates: FilterStates;
  hrisEmployees: HrisEmployee[];
  hrisProvider?: HrisProvider;
  selectedCandidates: ListCandidate[];
  totalFilteredCandidates: number;
  offerRequisitionFlowEnabled: boolean;
  onFilterStatesUpdate: (fs: FilterStates) => void;
  reloadPage: () => void;
  setAlert: (alert: AlertObject) => void;
  setSelectedCandidates: (candidates: ListCandidate[]) => void;
}

const columns = [
  {
    key: 'candidate',
    label: 'Candidate',
    type: 'string',
    isSortable: true,
    className: 'col-2',
  },
  {
    key: 'jobs',
    label: 'Job Applications',
    tooltipText: 'Candidate job applications.',
    type: 'string',
    isSortable: false,
    className: 'col-2',
  },
  {
    key: 'updated_at',
    label: 'Updated At',
    tooltipText: 'Latest candidate application update.',
    type: 'string',
  },
  { key: 'channel', label: 'Channel', type: 'string', isSortable: false },
  { key: 'stages', label: 'Current Stage', type: 'string', isSortable: false },
  {
    key: 'actions',
    label: 'Actions',
    tooltipText: 'Your pending actions.',
    type: 'string',
    isSortable: false,
  },
  {
    key: 'dropdown_menu',
    label: '',
    type: 'string',
    isSortable: false,
  },
];

function filterSelectableCandidates(candidates: ListCandidate[]) {
  return candidates
    .filter((candidate) => candidate.status !== 'draft')
    .filter((candidate) => candidate.application?.bulkActionsPermitted);
}

function addToSelectedList(
  selectedCandidates: ListCandidate[],
  candidates: ListCandidate[],
) {
  const combinedList = [
    ...selectedCandidates,
    ...filterSelectableCandidates(candidates),
  ];

  return combinedList.filter(
    (candidate, index, self) =>
      index === self.findIndex((c) => c.id === candidate.id),
  );
}

function removeFromSelectedList(
  selectedCandidates: ListCandidate[],
  candidates: ListCandidate[],
) {
  return selectedCandidates.filter(
    (c) => !candidates.find((candidate) => candidate.id === c.id),
  );
}

function isEntireListSelected(
  childList: ListCandidate[],
  parentList: ListCandidate[],
) {
  const selectableList = filterSelectableCandidates(childList);
  if (selectableList.length === 0) return false;

  return selectableList.every((c) =>
    parentList.map((selectedCandidate) => selectedCandidate.id).includes(c.id),
  );
}

function selectedCountText(selectedCandidates: ListCandidate[]) {
  const selectedCount = selectedCandidates.length;
  return selectedCount === 1
    ? `${selectedCount} candidate is selected`
    : `${selectedCount} candidates are selected`;
}

export function ListCandidatesTable(props: PropTypes) {
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
  const selectedCandidates = (checked: boolean) => {
    return checked
      ? addToSelectedList(props.selectedCandidates, props.candidates)
      : removeFromSelectedList(props.selectedCandidates, props.candidates);
  };

  const [candidateForAction, setCandidateForAction] =
    useState<ListCandidate>(null);

  const [pushToHrisModalActive, setPushToHrisModalActive] =
    useState<boolean>(false);

  const handlePushToHrisModalClose = () => {
    setPushToHrisModalActive(false);
    props.reloadPage();
  };

  const { atsEditBeforeExportToHrisEnabled } = useFeatureFlags();

  useEffect(() => {
    setIsHeaderCheckboxChecked(
      isEntireListSelected(props.candidates, props.selectedCandidates),
    );
  }, [props.candidates, props.selectedCandidates]);

  return (
    <>
      <Table className='mb-0 table-fixed'>
        <TableHeader
          columns={columns}
          checked={isHeaderCheckboxChecked}
          checkboxDisabled={
            filterSelectableCandidates(props.candidates).length === 0
          }
          onSelectedCandidatesUpdate={(checked: boolean) => {
            props.setSelectedCandidates(selectedCandidates(checked));
          }}
          sortColumn={props.filterStates.sortingColumn.columnName}
          sortDirection={props.filterStates.sortingColumn.direction}
          onSortUpdate={(column: string) => {
            props.onFilterStatesUpdate({
              ...props.filterStates,
              sortingColumn: {
                columnName: column,
                direction:
                  props.filterStates.sortingColumn.direction === 'asc'
                    ? 'desc'
                    : 'asc',
              },
            });
          }}
        />
        <TableBody
          atsSignableOfferEnabled={props.atsSignableOfferEnabled}
          candidates={props.candidates}
          currentUser={props.currentUser}
          emailAccountIntegration={props.emailAccountIntegration}
          hrisEmployees={props.hrisEmployees}
          hrisProvider={props.hrisProvider}
          selectedCandidates={props.selectedCandidates}
          offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
          setSelectedCandidates={props.setSelectedCandidates}
          reloadPage={props.reloadPage}
          setAlert={props.setAlert}
          candidateForAction={candidateForAction}
          setCandidateForAction={setCandidateForAction}
          setPushToHrisModalActive={setPushToHrisModalActive}
        />
      </Table>
      {props.totalFilteredCandidates > 0 ? (
        <TableFooter
          page={props.filterStates.page}
          pageSize={10}
          totalRows={props.totalFilteredCandidates}
          disabled={false}
          additionalInfo={
            props.selectedCandidates.length > 0 && (
              <span className='fw-semibold ms-4 text-dark-500'>
                {selectedCountText(props.selectedCandidates)}
              </span>
            )
          }
          onPageChanged={(newPage: number) =>
            props.onFilterStatesUpdate({
              ...props.filterStates,
              page: newPage,
            })
          }
        />
      ) : (
        props.emptyState
      )}
      {atsEditBeforeExportToHrisEnabled ? (
        <EditablePushToHrisModal
          applicationIds={[candidateForAction?.application?.id]}
          applicationId={candidateForAction?.application?.id}
          allowedHrisProvider={props.hrisProvider}
          isOpen={pushToHrisModalActive}
          onClose={handlePushToHrisModalClose}
          setAlert={props.setAlert}
        />
      ) : (
        <PushToHrisModal
          applicationIds={[candidateForAction?.application?.id]}
          allowedHrisProvider={props.hrisProvider}
          isOpen={pushToHrisModalActive}
          onClose={handlePushToHrisModalClose}
          setAlert={props.setAlert}
          candidateName={candidateForAction?.displayName}
          jobName={candidateForAction?.application?.job?.name}
        />
      )}
    </>
  );
}
